import { useEffect, useRef, useState } from 'react';

const START_AFTER = 4;  // px of movement before a press on the handle becomes a drag

/**
 * Dragging one thing up or down a short column - the lists in the sidebar,
 * the sections of a list. Not the to-do tree's drag: that one has levels,
 * nesting and a ghost, and this needs none of them. Same Pointer Events
 * approach, so a mouse and a finger are one code path.
 *
 * The column is the nearest `[data-reorder]` above the handle and its items
 * are the children carrying `data-reorder-item`; anything else in it (the
 * Inbox, "No section") is not counted and cannot be moved. While dragging,
 * `drag` is { group, from, to } - `to` counted among the OTHER items - which
 * the caller turns into a line above or below a row. Letting go calls
 * onDrop(group, from, to) when it moved; Escape cancels.
 */
export default function useReorder(onDrop) {
  const [drag, setDrag] = useState(null);
  const ref = useRef(null);
  const dropRef = useRef(onDrop);
  dropRef.current = onDrop;

  const start = (e, group, from) => {
    if (ref.current) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const box = e.currentTarget.closest('[data-reorder]');
    if (!box) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* synthetic */ }
    ref.current = { group, from, to: from, box, pointerId: e.pointerId, y0: e.clientY, started: false };
    setDrag({ group, from, to: from, started: false });
  };

  const live = Boolean(drag);
  useEffect(() => {
    if (!live) return undefined;
    const others = (d) => [...d.box.children]
      .filter((el) => el.hasAttribute('data-reorder-item'))
      .filter((el, i) => i !== d.from);
    const onMove = (e) => {
      const d = ref.current;
      if (!d || e.pointerId !== d.pointerId) return;
      if (!d.started) {
        if (Math.abs(e.clientY - d.y0) < START_AFTER) return;
        d.started = true;
        document.body.classList.add('ctd-dragging');
      }
      e.preventDefault();
      const to = others(d).filter((el) => {
        const r = el.getBoundingClientRect();
        return r.top + r.height / 2 < e.clientY;
      }).length;
      if (to !== d.to || !d.shown) {
        d.to = to;
        d.shown = true;
        setDrag({ group: d.group, from: d.from, to, started: true });
      }
    };
    const finish = (commit) => {
      const d = ref.current;
      ref.current = null;
      document.body.classList.remove('ctd-dragging');
      setDrag(null);
      if (commit && d && d.started && d.to !== d.from) dropRef.current(d.group, d.from, d.to);
    };
    const onUp = (e) => { if (ref.current && e.pointerId === ref.current.pointerId) finish(true); };
    const onCancel = (e) => { if (ref.current && e.pointerId === ref.current.pointerId) finish(false); };
    const onKey = (e) => { if (e.key === 'Escape') finish(false); };
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onKey);
    };
  }, [live]);

  /** The class for item `i` of `total` in `group`: where the line goes, if anywhere. */
  const markOf = (group, i, total) => {
    if (!drag || !drag.started || drag.group !== group) return '';
    if (i === drag.from) return ' is-reordering';
    const j = i > drag.from ? i - 1 : i;   // its place among the others
    if (j === drag.to) return ' is-drop-above';
    if (drag.to === total - 1 && j === total - 2) return ' is-drop-below';
    return '';
  };

  return { drag, start, markOf };
}
